import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

/**
 * Session cache for the desktop host OS version (PRODA-174).
 *
 * Source: Tauri IPC → Rust `os_info` (same value logged as `os_build`).
 * Examples: Windows `"10.0.22631"`, macOS `"14.5"`.
 *
 * Distinct from {@link getDesktopVersion} / `desktop_version`, which is the
 * Heidi app semver. Web always returns undefined; Snowplow emits `null`.
 *
 * Boot pattern mirrors {@link initDesktopVersion}: early events may omit the
 * value until IPC resolves. Do not block app or analytics boot for this field.
 *
 * The Tauri controller is loaded lazily inside {@link initDesktopOsInfo} so
 * importing the sync getter (used by Snowplow `client_context`) does not pull
 * the IPC module graph into unrelated analytics wiring tests.
 */

let cachedDesktopOsVersion: string | undefined;
let inFlightLoad: Promise<void> | undefined;
const MAX_LOAD_ATTEMPTS = 2;

export const getDesktopOsVersion = (): string | undefined => {
  if (!isTauri()) return undefined;
  return cachedDesktopOsVersion;
};

export const initDesktopOsInfo = async (): Promise<void> => {
  if (!isTauri()) return;
  if (cachedDesktopOsVersion) return;

  if (!inFlightLoad) {
    inFlightLoad = (async () => {
      const { systemController } = await import('@/tauri/controller/system');
      for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt += 1) {
        try {
          const version = await systemController.get_desktop_os_version();
          // Rust returns "" when os_info reports Unknown; keep the cache unset
          // so Snowplow continues to emit null rather than an empty string.
          if (!version) return;
          cachedDesktopOsVersion = version;
          return;
        } catch (err) {
          logger.error(
            `[desktopOsInfo] get_desktop_os_version() failed (attempt ${attempt}/${MAX_LOAD_ATTEMPTS}):`,
            err
          );
        }
      }
    })().finally(() => {
      if (!cachedDesktopOsVersion) {
        // Allow a later lifecycle call to start another bounded attempt sequence.
        inFlightLoad = undefined;
      }
    });
  }
  await inFlightLoad;
};
